import config from "../config";
import { customFetch } from "../lib/network/Axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

const BASE_URL = config.apiBaseUrl;

export type ReferralPerformanceFilters = {
	limit?: number;
	search?: string;
	page?: number;
	pageSize?: number;
	fromDate?: string;
	toDate?: string;
};

export const fetchDonationStats = createAsyncThunk(
	"campaigns/summary/stats",
	async () => {
		try {
			const response = await customFetch.get(
				BASE_URL + "/campaigns/summary/stats",
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const fetchCampaignBarsStats = createAsyncThunk(
	"campaigns/summary/stats?campaign=Y",
	async () => {
		try {
			const response = await customFetch.get(
				BASE_URL + "/campaigns/summary/stats?campaign=Y",
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const fetchShipmentStats = createAsyncThunk(
	"shipments/summary/stats",
	async (filters?: { fromDate?: string; toDate?: string }) => {
		try {
			const params = new URLSearchParams();
			if (filters?.fromDate?.trim()) {
				params.set('fromDate', filters.fromDate.trim());
			}
			if (filters?.toDate?.trim()) {
				params.set('toDate', filters.toDate.trim());
			}
			const response = await customFetch.get(
				BASE_URL + `/shipments/summary/stats${params.toString() ? `?${params.toString()}` : ''}`,
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const fetchDonorsStats = createAsyncThunk(
	"donors/summary/stats",
	async (filters?: { fromDate?: string; toDate?: string }) => {
		try {
			const params = new URLSearchParams();
			if (filters?.fromDate?.trim()) {
				params.set('fromDate', filters.fromDate.trim());
			}
			if (filters?.toDate?.trim()) {
				params.set('toDate', filters.toDate.trim());
			}
			const response = await customFetch.get(
				BASE_URL + `/donors/summary/stats${params.toString() ? `?${params.toString()}` : ''}`,
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const fetchDonorsInfo = createAsyncThunk(
	"donors/summary/info",
	async () => {
		try {
			const response = await customFetch.get(BASE_URL + "/donors/summary/info");
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);
export const fetchLeaderBoardInfo = createAsyncThunk(
	"campaigns/summary/leadboard",
	async () => {
		try {
			const response = await customFetch.get(
				BASE_URL + "/campaigns/summary/leadboard",
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const fetchReferralLeaderboard = createAsyncThunk(
	"donors/referrals/leaderboard",
	async (filters: ReferralPerformanceFilters = { limit: 5 }) => {
		try {
			const params = new URLSearchParams();
			const limit = filters.limit ?? 5;
			params.set('limit', String(limit));

			if (filters.fromDate?.trim()) {
				params.set('fromDate', filters.fromDate.trim());
			}

			if (filters.toDate?.trim()) {
				params.set('toDate', filters.toDate.trim());
			}

			const response = await customFetch.get(
				BASE_URL + `/dashboard/referrals?${params.toString()}`,
			);
			return response;
		} catch (err) {
			console.log(err);
		}
	},
);

export const getReferralPerformances = async (
	filters: ReferralPerformanceFilters = {},
) => {
	const params = new URLSearchParams();

	if (filters.limit) {
		params.set("limit", String(filters.limit));
	}

	if (filters.search?.trim()) {
		params.set("search", filters.search.trim());
	}

	if (filters.page) {
		params.set("page", String(filters.page));
	}

	if (filters.pageSize) {
		params.set("pageSize", String(filters.pageSize));
	}

	if (filters.fromDate?.trim()) {
		params.set("fromDate", filters.fromDate.trim());
	}

	if (filters.toDate?.trim()) {
		params.set("toDate", filters.toDate.trim());
	}

	const query = params.toString();
	return customFetch.get(
		BASE_URL + `/dashboard/referrals${query ? `?${query}` : ""}`,
	);
};

export const getReferredListForUser = async (
	userId: number,
	filters: Pick<ReferralPerformanceFilters, 'fromDate' | 'toDate'> = {},
) => {
	const params = new URLSearchParams();

	if (filters.fromDate?.trim()) {
		params.set('fromDate', filters.fromDate.trim());
	}

	if (filters.toDate?.trim()) {
		params.set('toDate', filters.toDate.trim());
	}

	const query = params.toString();
	return customFetch.get(
		BASE_URL + `/dashboard/referrals/${userId}/referred-list${query ? `?${query}` : ''}`,
	);
};

export const updateUserReferralCode = async (
	userId: number,
	referralCode: string,
) => {
	return customFetch.patch(
		BASE_URL + `/dashboard/referrals/${userId}/referral-code`,
		{ referralCode },
	);
};

export const getReferralTreeByUser = async (
	userId: number,
	maxDepth: number = 6,
	filters: Pick<ReferralPerformanceFilters, 'fromDate' | 'toDate'> = {},
) => {
	const params = new URLSearchParams();
	params.set('maxDepth', String(maxDepth));

	if (filters.fromDate?.trim()) {
		params.set('fromDate', filters.fromDate.trim());
	}

	if (filters.toDate?.trim()) {
		params.set('toDate', filters.toDate.trim());
	}

	return customFetch.get(
		BASE_URL + `/dashboard/referrals/tree/${userId}?${params.toString()}`,
	);
};

export type DashboardFilters = {
	fromDate: string; // YYYY-MM-DD
	toDate: string;   // YYYY-MM-DD
	campaignIds?: number[];
};

export const getFilteredDashboardStatsRequest = async (filters: DashboardFilters) => {
	const params = new URLSearchParams();
	params.set('fromDate', filters.fromDate);
	params.set('toDate', filters.toDate);
	if (filters.campaignIds && filters.campaignIds.length > 0) {
		params.set('campaignIds', filters.campaignIds.join(','));
	}
	return customFetch.get(
		BASE_URL + `/dashboard/stats?${params.toString()}`,
	);
};

export const getRegistrationsExport = async (filters: DashboardFilters) => {
	const params = new URLSearchParams();
	params.set('fromDate', filters.fromDate);
	params.set('toDate', filters.toDate);
	if (filters.campaignIds && filters.campaignIds.length > 0) {
		params.set('campaignIds', filters.campaignIds.join(','));
	}
	return customFetch.get(
		BASE_URL + `/dashboard/export/registrations?${params.toString()}`,
	);
};

export const getDonationsExport = async (filters: DashboardFilters) => {
	const params = new URLSearchParams();
	params.set('fromDate', filters.fromDate);
	params.set('toDate', filters.toDate);
	if (filters.campaignIds && filters.campaignIds.length > 0) {
		params.set('campaignIds', filters.campaignIds.join(','));
	}
	return customFetch.get(
		BASE_URL + `/dashboard/export/donations?${params.toString()}`,
	);
};

export const getDashboardP1InsightsRequest = async (filters: DashboardFilters) => {
	const params = new URLSearchParams();
	params.set('fromDate', filters.fromDate);
	params.set('toDate', filters.toDate);
	if (filters.campaignIds && filters.campaignIds.length > 0) {
		params.set('campaignIds', filters.campaignIds.join(','));
	}
	return customFetch.get(
		BASE_URL + `/dashboard/insights/p1?${params.toString()}`,
	);
};

export const getDashboardP2InsightsRequest = async (filters: DashboardFilters) => {
	const params = new URLSearchParams();
	params.set('fromDate', filters.fromDate);
	params.set('toDate', filters.toDate);
	if (filters.campaignIds && filters.campaignIds.length > 0) {
		params.set('campaignIds', filters.campaignIds.join(','));
	}
	return customFetch.get(
		BASE_URL + `/dashboard/insights/p2?${params.toString()}`,
	);
};

export const fetchFilteredDashboardStats = createAsyncThunk(
	'dashboard/filteredStats',
	async (filters: DashboardFilters) => {
		const response = await getFilteredDashboardStatsRequest(filters);
		return response;
	},
);

export const fetchDashboardCampaigns = createAsyncThunk(
	'dashboard/campaignsList',
	async () => {
		const response = await customFetch.get(BASE_URL + '/dashboard/campaigns-list');
		return response;
	},
);
